import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import API from '../api';
import BackButton from './common/BackButton';
import { SEMANTIC } from '../styles/theme';

export default function AiAdvisor() {
  const userId = localStorage.getItem('userId');
  const { t } = useTranslation();

  const [membership, setMembership] = useState(null);
  const [tradingMode, setTradingMode] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    Promise.all([
      API.get(`/users/${userId}/profile`),
      API.get('/membership-tiers'),
    ])
      .then(([profileRes, tiersRes]) => {
        setMembership(profileRes.data.membership || 'earth');
        setTradingMode(profileRes.data.trading_mode || 'paper');
        setTiers(tiersRes.data || []);
      })
      .catch(() => { setMembership('earth'); setTradingMode('paper'); });
  }, [userId]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput('');

    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setLoading(true);

    const history = messages.filter(m => m.role === 'user' || m.role === 'assistant');

    try {
      const res = await API.post('/ai/advisor', {
        user_id: Number(userId),
        question: q,
        history,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.answer || '' }]);
    } catch (e) {
      const errMsg = e.response?.data?.error || 'Errore nella risposta AI';
      setMessages(prev => [...prev, { role: 'error', content: errMsg }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: SEMANTIC.backgroundApp,
      color: SEMANTIC.textPrimary,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px 12px',
        borderBottom: '1px solid rgba(255,215,0,0.15)',
        backgroundColor: SEMANTIC.backgroundCard,
        flexShrink: 0,
      }}>
        <BackButton to="/portfolio" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <span style={{ fontSize: 28 }}>🪐</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#ffd700' }}>AI Advisor</div>
            <div style={{ fontSize: 11, color: '#888', letterSpacing: 1 }}>
              {tiers.filter(t => t.ai_advisor).length > 0
                ? `ESCLUSIVO ${tiers.filter(t => t.ai_advisor).map(t => t.display_name.toUpperCase()).join(' / ')}`
                : 'FUNZIONE PREMIUM'}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      {membership === null || tradingMode === null ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#888' }}>Caricamento...</span>
        </div>
      ) : (() => {
        const aiTiers = tiers.filter(t => t.ai_advisor);
        const currentTier = tiers.find(t => t.tier_key === membership);
        // Paper trading = profilo Saturn: accesso a tutte le funzionalità
        const hasAccess = tradingMode === 'paper' || currentTier?.ai_advisor;

        if (!hasAccess) {
          const aiTierNames = aiTiers.map(t => t.display_name).join(', ');
          const accentColor = aiTiers[0]?.color || '#ffd700';

          return (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '40px 24px', textAlign: 'center',
            }}>
              {/* Icone di tutti i tier con AI Advisor */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'center' }}>
                {aiTiers.map((t, i) => (
                  <div key={t.tier_key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    {t.icon_type === 'img'
                      ? <img src={t.icon_value} alt={t.display_name} style={{ width: 56, height: 56, borderRadius: '50%', border: `2px solid ${t.color}` }} />
                      : <span style={{ fontSize: 52, lineHeight: 1 }}>{t.icon_value || '🪐'}</span>}
                    <span style={{ fontSize: 11, fontWeight: 700, color: t.color, letterSpacing: 0.5 }}>{t.display_name.toUpperCase()}</span>
                  </div>
                ))}
              </div>
              <h3 style={{ color: accentColor, marginBottom: 12 }}>
                Funzione esclusiva {aiTierNames}
              </h3>
              <p style={{ color: '#aaa', maxWidth: 340, lineHeight: 1.6 }}>
                L'AI Advisor è disponibile solo per i membri {aiTierNames}.
                Effettua l'upgrade per accedere a consigli personalizzati sul tuo portafoglio.
              </p>
              <div style={{
                marginTop: 24, padding: '10px 18px', borderRadius: 12,
                backgroundColor: `${accentColor}14`,
                border: `1px solid ${accentColor}40`,
                color: accentColor, fontSize: 13,
              }}>
                Membership attuale: <strong style={{ textTransform: 'capitalize' }}>{currentTier?.display_name || membership}</strong>
              </div>
            </div>
          );
        }
        return null;
      })() || (
        /* Chat */
        <>
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '16px 16px 0',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {messages.length === 0 ? (
              <div>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>{t('ai_advisor.start_prompt')}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {t('ai_advisor.quick_questions', { returnObjects: true }).map(q => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      disabled={loading}
                      style={{
                        padding: '8px 16px', borderRadius: 20, fontSize: 13,
                        backgroundColor: 'rgba(255,215,0,0.08)',
                        border: '1px solid rgba(255,215,0,0.3)',
                        color: '#ffd700', cursor: 'pointer',
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '85%', padding: '10px 14px', fontSize: 14, lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    ...(msg.role === 'user' ? {
                      backgroundColor: 'rgba(255,215,0,0.15)',
                      border: '1px solid rgba(255,215,0,0.3)',
                      color: SEMANTIC.textPrimary,
                    } : msg.role === 'error' ? {
                      backgroundColor: 'rgba(255,68,68,0.1)',
                      border: '1px solid rgba(255,68,68,0.3)',
                      color: '#ff6666',
                    } : {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: SEMANTIC.textPrimary,
                    }),
                  }}>
                    {msg.role === 'assistant' && (
                      <div style={{ fontSize: 10, color: '#ffd700', marginBottom: 6, fontWeight: 700 }}>
                        🪐 AI Advisor
                      </div>
                    )}
                    {msg.content}
                  </div>
                </div>
              ))
            )}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 16px', borderRadius: '16px 16px 16px 4px',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', gap: 5, alignItems: 'center',
                }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: 7, height: 7, borderRadius: '50%',
                      backgroundColor: '#ffd700',
                      animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      opacity: 0.7,
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: SEMANTIC.backgroundCard,
            display: 'flex', gap: 8, alignItems: 'center',
            flexShrink: 0,
          }}>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Scrivi un messaggio..."
              disabled={loading}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 22,
                border: '1px solid rgba(255,215,0,0.25)',
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: SEMANTIC.textPrimary, fontSize: 14, outline: 'none',
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              style={{
                width: 42, height: 42, borderRadius: '50%', border: 'none',
                backgroundColor: loading || !input.trim() ? 'rgba(255,215,0,0.2)' : '#ffd700',
                color: loading || !input.trim() ? '#888' : '#000',
                cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, flexShrink: 0,
              }}
            >
              <i className="bi bi-send-fill" />
            </button>
          </div>
        </>
      )}

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
